import { Cash, Commission, Interest, Order, Result } from '~/api-services/ReportsService/types';
import { ExportColumn } from '~/hooks/useReportExport';
import i18n from '~/i18n';
import { stripGroupSeparators } from './csv';
import { formatDate } from './format';

const num = (value: string | undefined | null) => stripGroupSeparators(value);

const capitalize = (value: string | undefined | null) =>
    value ? value.charAt(0).toUpperCase() + value.substring(1) : '';

const aiColumn = <T extends { strategyId: string | null }>(): ExportColumn<T> => ({
    header: 'export-ai',
    value: (r) => i18n.t(r.strategyId ? 'yes' : 'no'),
});

const withAi = <T extends { strategyId: string | null }>(columns: ExportColumn<T>[], aiEnabled: boolean) =>
    aiEnabled ? [...columns, aiColumn<T>()] : columns;

export const resultExportColumns = (aiEnabled: boolean): ExportColumn<Result>[] =>
    withAi(
        [
            { header: 'export-close-time', value: (r) => formatDate(r.timestamp) },
            { header: 'export-open-time', value: (r) => formatDate(r.openTime) },
            { header: 'order-instrument', value: (r) => r.symbolNameTranslated },
            { header: 'position-direction', value: (r) => capitalize(r.side) },
            { header: 'position-volume', value: (r) => num(r.volumeFormatted) },
            { header: 'export-open-price', value: (r) => num(r.openPriceFormatted) },
            { header: 'export-close-price', value: (r) => num(r.closePriceFormatted) },
            { header: 'export-currency', value: (r) => r.currency },
            { header: 'result', value: (r) => num(r.resultFormatted) },
            { header: 'export-account-currency', value: (_r, ctx) => ctx.currency },
            { header: 'order-number', value: (r) => r.orderNo },
        ],
        aiEnabled
    );

export const cashExportColumns = (_aiEnabled: boolean): ExportColumn<Cash>[] => [
    { header: 'export-date-time', value: (c) => formatDate(c.timestamp) },
    { header: 'details-label', value: (c) => c.typeTranslated },
    { header: 'position-direction', value: (c) => c.sideTranslated },
    { header: 'amount-label', value: (c) => num(c.amountFormatted) },
    { header: 'export-account-currency', value: (_c, ctx) => ctx.currency },
];

export const ordersExportColumns = (aiEnabled: boolean): ExportColumn<Order>[] =>
    withAi(
        [
            { header: 'position-order-time', value: (o) => formatDate(o.timestamp) },
            { header: 'position-modif-time', value: (o) => formatDate(o.modificationTimestamp) },
            { header: 'order-instrument', value: (o) => o.symbolNameTranslated },
            { header: 'position-direction', value: (o) => capitalize(o.side) },
            { header: 'position-volume', value: (o) => num(o.volumeFormatted) },
            { header: 'order-price', value: (o) => num(o.orderPriceFormatted) },
            { header: 'execution-price', value: (o) => num(o.executionPriceFormatted) },
            { header: 'export-currency', value: (o) => o.currency },
            { header: 'order-type', value: (o) => o.typeTranslated },
            { header: 'order-status', value: (o) => o.statusTranslated },
            { header: 'order-number', value: (o) => o.orderNumber },
        ],
        aiEnabled
    );

export const commissionsExportColumns = (aiEnabled: boolean): ExportColumn<Commission>[] =>
    withAi(
        [
            { header: 'position-order-time', value: (c) => formatDate(c.timestamp) },
            { header: 'order-instrument', value: (c) => c.symbolNameTranslated },
            { header: 'position-direction', value: (c) => capitalize(c.side) },
            { header: 'position-volume', value: (c) => num(c.volumeFormatted) },
            { header: 'export-currency', value: (c) => c.currency },
            { header: 'commissions-label', value: (c) => num(c.amountFormatted) },
            { header: 'export-account-currency', value: (_c, ctx) => ctx.currency },
            { header: 'order-number', value: (c) => c.orderNumber },
        ],
        aiEnabled
    );

export const interestExportColumns = (aiEnabled: boolean): ExportColumn<Interest>[] =>
    withAi(
        [
            { header: 'export-date-time', value: (i) => formatDate(i.timestamp) },
            { header: 'order-instrument', value: (i) => i.symbolNameTranslated },
            { header: 'order-type', value: (i) => i.typeTranslated },
            { header: 'position-direction', value: (i) => i.sideTranslated },
            { header: 'position-volume', value: (i) => num(i.volumeFormatted) },
            { header: 'export-currency', value: (i) => i.currency },
            { header: 'amount-label', value: (i) => num(i.amountFormatted) },
            { header: 'export-account-currency', value: (_i, ctx) => ctx.currency },
        ],
        aiEnabled
    );
